import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Book, Search, Zap, ArrowLeft, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { helpCategories } from '../lib/helpArticles';

const matches = (article, query) =>
  article.title.toLowerCase().includes(query) ||
  article.body.some((p) => p.toLowerCase().includes(query));

// Lines written as "1. Do the thing" render as steps.
const isStep = (line) => /^\d+\.\s/.test(line);

function ArticleBody({ body }) {
  return (
    <div className="space-y-2 pt-2 pb-1">
      {body.map((line, i) =>
        isStep(line) ? (
          <p key={i} className="text-sm text-muted-foreground pl-4 -indent-4 leading-relaxed">
            <span className="font-semibold text-foreground">{line.slice(0, line.indexOf(' '))}</span>
            {line.slice(line.indexOf(' '))}
          </p>
        ) : (
          <p key={i} className="text-sm text-muted-foreground leading-relaxed">{line}</p>
        )
      )}
    </div>
  );
}

function ArticleItem({ article, categoryId, index, forceOpen }) {
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;
  const testId = `help-article-${categoryId}-${index}`;

  return (
    <li className="border-b last:border-b-0 border-slate-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-2.5 flex items-start justify-between gap-2 group"
        aria-expanded={expanded}
        data-testid={`${testId}-toggle`}
      >
        <span className="text-sm text-blue-600 group-hover:underline">{article.title}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && (
        <div data-testid={`${testId}-body`}>
          <ArticleBody body={article.body} />
        </div>
      )}
    </li>
  );
}

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const query = searchQuery.trim().toLowerCase();

  const filteredCategories = helpCategories
    .map((category) => ({
      ...category,
      articles: query ? category.articles.filter((a) => matches(a, query)) : category.articles,
    }))
    .filter((category) => category.articles.length > 0);

  const resultCount = filteredCategories.reduce((n, c) => n + c.articles.length, 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Taptile" className="w-8 h-8" />
            <span className="text-xl font-bold font-heading">Taptile</span>
          </Link>
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full">
              <Book className="h-12 w-12 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4">Help Center</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Everything you need to know about using Taptile. Click any question to read the answer.
          </p>

          {/* Search */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-lg"
              data-testid="help-search-input"
            />
          </div>
          {query && (
            <p className="text-sm text-muted-foreground mt-3" data-testid="help-search-count">
              {resultCount} {resultCount === 1 ? 'article' : 'articles'} matching “{searchQuery}”
            </p>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="container mx-auto px-4 py-16">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12" data-testid="help-no-results">
            <p className="text-muted-foreground text-lg mb-4">No articles found matching your search.</p>
            <Link to="/contact">
              <Button variant="outline">Ask us instead</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {filteredCategories.map((category) => (
              <Card key={category.id} className="hover:shadow-lg transition-shadow" data-testid={`help-category-${category.id}`}>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    {category.icon}
                    <CardTitle className="text-lg">{category.title}</CardTitle>
                  </div>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul>
                    {category.articles.map((article, idx) => (
                      <ArticleItem
                        key={article.title}
                        article={article}
                        categoryId={category.id}
                        index={idx}
                        forceOpen={!!query}
                      />
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Contact Support */}
      <div className="bg-blue-50 py-16">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <Zap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold font-heading mb-4">Still need help?</h2>
          <p className="text-lg text-muted-foreground mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg">
                Contact Support
              </Button>
            </Link>
            <a href="mailto:phil@dynatexsaas.com">
              <Button size="lg" variant="outline">
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
